import { t } from "../../hooks/useAppTranslation";
import { Form, Formik } from "formik";
import styles from "./ExternalControlStyle.module.scss";
import Input from "../Input/Input";
import * as Yup from "yup";
import SelectCustom from "../SelectCustom/SelectCustom";
import DatePickerCustom from "../DatePickerCustom/DatePickerCustom";
import RadioCustom from "../RadioCustom/RadioCustom";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import { ISubject, SubjectFormHelper } from "./SubjectFormHelper";
import { IPZPControl, PZPFormHelper } from "./PZPFormHelper";
import React, { useEffect, useRef, useState } from "react";
import { ConsequencesFormHelper, IConseqeunce } from "./ConsequencesFormHelper";
import { FormActionButtons } from "../FormActionButtons/FormActionButtons";
import {
  ICustomSelectValues,
  IDictionary,
  IFields,
} from "./ExternalControlContainer";
import {
  ExternalControlFormDocuments
} from "./ExternalControlFormDocuments";
import { BlockUnsavedForm } from "../BlockUnsavedForm/BlockUnsavedForm";
import {handleDatePickedFrom, handleDatePickedTo} from "../../Helpers/utils";

export interface FormValues extends IFields {
  subjects?: ISubject[];
  pzpControls?: IPZPControl[];
  consequences?: IConseqeunce[];
}

interface Props extends FormValues {
  controlResults: IDictionary[];
  subjectTypes: IDictionary[];
  projectUuid?: String;
  pzpTypes: IDictionary[];
  consequencesTypes: IDictionary[];
  consequencesPercents: IDictionary[];
  controllingSubjects: IDictionary[];
  controlUuid: String | null;

  mode: string;
  disableSubmit: boolean;

  addNewSubject?(): void;
  handleSave(values: object): void;
  setDisableSubmit(value: boolean): void;
}

export interface ISelectSearchProps {
  props: {
    options: ICustomSelectValues[];
  };
  state: {
    search: string;
  }
}

export const ExternalControlForm: React.FC<Props> = ({
  /* DICTS */
  subjectTypes,
  pzpTypes,
  consequencesTypes,
  consequencesPercents,
  controllingSubjects,
  controlResults,
  controlUuid,

  /* VALUES */
  ownnumber,
  dateFrom,
  dateTo,
  description,
  typeId,
  typeDescription,
  controllingSubjectId,
  controlResultId,
  controlResult,
  controllingSubject,
  controlledSubject,
  pzpControl,
  hasConsequences,
  subjects,
  projectUuid,
  pzpControls,
  consequences,

  mode,
  disableSubmit,

  /* OTHERS */
  addNewSubject,
  setDisableSubmit,
  handleSave,
}) => {
  const FormSchema = Yup.object().shape({
    ownnumber: Yup.string().required(t('g-enter-value')),
    dateFrom: Yup.date().required(t('g-select-date')),
    dateTo: Yup.date().required(t('g-select-date')),
    description: Yup.string().required(t('g-enter-value')),
    typeId: Yup.boolean().required(t('g-enter-value')),
    controllingSubjectId: Yup.string().required(t('g-enter-value')),
    controlResultId: Yup.string().required(t('g-enter-value')),
  });

  const customSelectRef1 = useRef(null);
  const customSelectRef2 = useRef(null);
  const datePickerRef1 = useRef(null);
  const datePickerRef2 = useRef(null);

  const [tempControlllingSubject, setTempControllingSubject] =
    useState<ICustomSelectValues>();

  const [tempControlResult, setTempControlResult] =
    useState<ICustomSelectValues>();

  const [previousSearch, setPreviousSearch] = useState('');
  const [previousSearchWynikKontroli, setPreviousSearchWynikKontroli] = useState('');

  useEffect(() => {
    if (mode === "edit" || "copy") {
      if (controllingSubjectId != undefined && controllingSubject) {
        setTempControllingSubject({
          label: controllingSubject,
          value: controllingSubjectId,
        });
      }

      if (controlResult && controlResultId != undefined) {
        setTempControlResult({
          value: controlResultId,
          label: controlResult,
        });
      }
    }
  }, [
    controlResultId,
    controlResults.length,
    controllingSubjectId,
    controllingSubjects.length,
  ]);

  const formikRef = useRef<any>(null);

  return (
    <Formik
      innerRef={formikRef}
      enableReinitialize={true}
      initialValues={{
        ownnumber: ownnumber || "",
        dateFrom: dateFrom || "",
        dateTo: dateTo || "",
        description: description || "",
        typeId: typeId || "",
        typeDescription: typeDescription || "",
        controllingSubjectId: controllingSubjectId,
        controlResultId: controlResultId,
        controlledSubject: controlledSubject || false,
        pzpControl: pzpControl || false,
        hasConsequences: hasConsequences || false,
        subjects: subjects || [],
        pzpControls: pzpControls || [],
        consequences: consequences || [],
      }}
      validationSchema={FormSchema}
      validate={(values) => {
        const errors: any = {};
        if (values.typeId === "1" && !values.typeDescription?.trim()) {
          errors.typeDescription = t('g-enter-value');
        }
        return errors;
      }}
      onSubmit={(values, { setFieldTouched }) => {
        if (values.typeId === "1") {
          setFieldTouched("typeDescription", true, false);
        }
        handleSave(values);
      }}
    >
      {({ handleChange, setFieldValue, setFieldTouched, values, errors, touched, validateForm, submitForm, setTouched, setFieldError }) => {
        const handleFormSubmit = async (e: React.FormEvent) => {
          e.preventDefault();
          const allTouched: any = {};
          Object.keys(values).forEach(key => (allTouched[key] = true));
          setTouched(allTouched, false);

          const formErrors = await validateForm();
          if (Object.keys(formErrors).length === 0) {
            submitForm();
          }
        }

        return (
          <>
            <BlockUnsavedForm />
            <Form onChange={() => disableSubmit && setDisableSubmit(false)} onSubmit={handleFormSubmit}>
              <h3 className={styles.headerLabel}>{t('external-controls-details-control')}</h3>
              <div className='row'>
                <div className='col-md-6'>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={values.controlledSubject}
                        onChange={handleChange}
                        name='controlledSubject'
                      />
                    }
                    label={t('external-controls-controlled-entity')}
                  />
                </div>
              </div>
              {values.controlledSubject && (
                <SubjectFormHelper
                  subjectTypes={subjectTypes}
                  subjects={values.subjects}
                  setFieldValue={setFieldValue}
                />
              )}
              <div className='row'>
                <div className='col-md-12 text-right'>
                  {addNewSubject && (
                    <button
                      className={`btn save-btn ${styles.saveButtonSmall} uppercase`}
                      type='button'
                      onClick={() => addNewSubject()}
                    >
                      + {t('external-controls-controlling-body')}
                    </button>
                  )}
                </div>
              </div>
              <div className='row'>
                <div className='col-md-6'>
                  <Input
                    inputId='ext-cnt-ownnumber'
                    name='ownnumber'
                    label={t('external-controls-reference-control-number')}
                    placeholder={t('external-controls-reference-control-number')}
                    maxLength={50}
                    type='text'
                    formik={true}
                    chartsCount={values.ownnumber.length}
                    onChange={handleChange}
                    error={
                      errors.ownnumber && touched.ownnumber
                        ? errors.ownnumber
                        : undefined
                    }
                    invalid={Boolean(errors.ownnumber && touched.ownnumber)}
                    errorId={"id" + errors.ownnumber}
                  />
                </div>
                <div className='col-md-6'>
                  <SelectCustom
                    id='ext-cnt-controllingSubject'
                    placeholder={t('external-controls-controlling-body')}
                    options={controllingSubjects.map((el) => {
                      return {
                        label: el.description,
                        value: el.entryId,
                      };
                    })}
                    values={
                      tempControlllingSubject
                        ? [
                            {
                              label: tempControlllingSubject.label,
                              value: tempControlllingSubject.value,
                            },
                          ]
                        : []
                    }
                    onChange={(value: ICustomSelectValues[]) => {
                      setTempControllingSubject(value[0]);
                      setFieldValue("controllingSubjectId", value[0]?.value);
                    }}
                    searchFn={(selectProps:ISelectSearchProps) => {
                      const options = selectProps.props?.options;
                      const includesOptions = options?.find((el: ICustomSelectValues) => el.label.toUpperCase().includes(selectProps.state.search.toUpperCase()));
                      (includesOptions && !values.controllingSubjectId) ? setPreviousSearch(selectProps.state.search) : selectProps.state['search'] = previousSearch;
                    }}
                    keepSelectedInList={true}
                    containerClassName={styles.selectContainer}
                    labelClassName={styles.selectCustomLabel}
                    color='#0d47a1'
                    error={
                      errors.controllingSubjectId &&
                      touched.controllingSubjectId
                        ? errors.controllingSubjectId
                        : undefined
                    }
                    invalid={Boolean(
                      errors.controllingSubjectId &&
                        touched.controllingSubjectId
                    )}
                    errorId={"id" + errors.controllingSubjectId}
                    label={t('external-controls-controlling-body')}
                    labelHeader={false}
                    isMulti={false}
                    onClear=''
                    dropdownId=''
                    disabled={false}
                    parentRef={customSelectRef1}
                    className=''
                    optionNameVisible={undefined}
                  />
                </div>
              </div>

              <div className='row'>
                <div className='col-md-6'>
                  <div className='row'>
                    <div className='col-md-6'>
                      <DatePickerCustom
                        id='ext-cnt-dateFrom'
                        name='dateFrom'
                        label={t('external-controls-control-date')}
                        className=''
                        containerClassName=''
                        invalid={errors.dateFrom && touched.dateFrom}
                        error={
                          errors.dateFrom && touched.dateFrom && errors.dateFrom
                        }
                        errorId='dateAlertFrom'
                        labelHeader=''
                        selected={values.dateFrom}
                        onChange={(val: string) => {
                          const { dateFrom, dateTo } = handleDatePickedFrom(val);
                          setFieldValue("dateFrom", dateFrom);
                          setFieldValue("dateTo", dateTo);
                        }}
                        placeholderText='yyyy-mm-dd'
                        dateFormat='yyyy-MM-dd'
                        labelClassName='date-picker__label--in-fieldset'
                        parentRef={datePickerRef1}
                      />
                    </div>
                    <div className='col-md-6'>
                      <DatePickerCustom
                        id='ext-cnt-dateTo'
                        name='dateTo'
                        label='&nbsp;'
                        className=''
                        containerClassName=''
                        invalid={errors.dateTo && touched.dateTo}
                        error={errors.dateTo && touched.dateTo && errors.dateTo}
                        errorId='dateAlertTo'
                        labelHeader=''
                        selected={values.dateTo}
                        onChange={(val: string) => {
                          setFieldValue("dateTo", handleDatePickedTo(val))
                        }}
                        dateFormat='yyyy-MM-dd'
                        labelClassName='date-picker__label--in-fieldset'
                        parentRef={datePickerRef2}
                      />
                    </div>
                  </div>
                </div>
                <div className='col-md-6'>
                  <div className='row'>
                    <div className='col-md-12'>
                      <p className={styles.radioLabelHeader}>{t('external-controls-control-type')}</p>
                    </div>
                  </div>

                  <div className='row'>
                    <div className='col-md-6'>
                      <RadioCustom
                        label={t('external-controls-audit-operation')}
                        id='ext-cnt-type-radio-1'
                        value='0'
                        checked={values.typeId === "0"}
                        labelClassName={styles.radioLabelClass}
                        onChange={(e: any) => {
                          handleChange(e);
                          setFieldValue("typeDescription", "");
                          setFieldTouched("typeDescription", false, false);
                          setFieldError("typeDescription", undefined);
                        }}
                        name='typeId'
                        error={
                          errors.typeId && touched.typeId
                            ? errors.typeId
                            : undefined
                        }
                        errorId={"id" + errors.typeId}
                        invalid={Boolean(errors.typeId && touched.typeId)}
                      />
                    </div>
                    <div className='col-md-6'>
                      <RadioCustom
                        label={t('external-controls-other')}
                        id='ext-cnt-type-radio-2'
                        value='1'
                        checked={values.typeId === "1"}
                        labelClassName={styles.radioLabelClass}
                        onChange={handleChange}
                        name='typeId'
                        errorId={"id" + errors.typeId + "2"}
                        invalid={Boolean(errors.typeId && touched.typeId)}
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className='row' style={{ justifyContent: 'flex-end'}}>
                <div className='col-md-6'>
                  <Input
                    inputId='ext-cnt-typeDescription'
                    name='typeDescription'
                    label={t('external-controls-control-type-description')}
                    placeholder={t('external-controls-control-type-description')}
                    maxLength={50}
                    type='text'
                    formik={true}
                    chartsCount={values.typeDescription?.length || 0}
                    onChange={handleChange}
                    disabled={values.typeId !== "1"}
                    error={
                      errors.typeDescription && touched.typeDescription
                        ? errors.typeDescription
                        : undefined
                    }
                    invalid={Boolean(errors.typeDescription && touched.typeDescription)}
                    errorId={"id" + errors.typeDescription}
                  />
                </div>
              </div>
              <div className='row'>
                <div className='col-md-6'>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={values.pzpControl}
                        onChange={handleChange}
                        name='pzpControl'
                      />
                    }
                    label={t('external-controls-public-procurement-controls')}
                  />
                </div>
              </div>
              {values.pzpControl && (
                <PZPFormHelper
                  pzpTypes={pzpTypes}
                  pzpControls={values.pzpControls}
                  setFieldValue={setFieldValue}
                />
              )}

              <h3 className={styles.headerLabel}>{t('external-controls-control-result')}</h3>
              <div className='row'>
                <div className='col-md-6'>
                  <SelectCustom
                    options={controlResults.map((el) => {
                      return {
                        label: el.description,
                        value: el.entryId,
                      };
                    })}
                    values={
                      tempControlResult
                        ? [
                            {
                              label: tempControlResult.label,
                              value: tempControlResult.value,
                            },
                          ]
                        : []
                    }
                    onChange={(value: ICustomSelectValues[]) => {
                      setTempControlResult(value[0]);
                      setFieldValue("controlResultId", value[0]?.value);
                    }}
                    searchFn={(selectProps:ISelectSearchProps) => {
                      const options = selectProps.props?.options;
                      const includesOptions = options?.find((el: ICustomSelectValues) => el.label.toUpperCase().includes(selectProps.state.search.toUpperCase()));
                      (includesOptions && !values.controlResultId) ? setPreviousSearchWynikKontroli(selectProps.state.search) : selectProps.state['search'] = previousSearchWynikKontroli;
                    }}
                    error={touched.controlResultId && errors.controlResultId}
                    invalid={errors.controlResultId && touched.controlResultId}
                    placeholder={t('external-controls-control-result')}
                    containerClassName={styles.selectContainer}
                    labelClassName={styles.selectCustomLabel}
                    label={t('external-controls-control-result')}
                    errorId='ext-cnt-controlResult'
                    id='ext-cnt-controlResultId'
                    labelHeader={false}
                    isMulti={false}
                    keepSelectedInList
                    onClear={false}
                    handle
                    dropdownId=''
                    disabled={false}
                    parentRef={customSelectRef2}
                    className=''
                    optionNameVisible={undefined}
                  />
                </div>
              </div>
              <div className='row'>
                <div className='col-md-12'>
                  <Input
                    inputId='ext-cnt-description'
                    name='description'
                    label={t('external-controls-control-result-description')}
                    placeholder={t('external-controls-description')}
                    maxLength={4000}
                    component='textarea'
                    rows={3}
                    formik={true}
                    chartsCount={values.description.length}
                    onChange={handleChange}
                    containerClassName={styles.resizableTextarea}
                    error={
                      errors.description && touched.description
                        ? errors.description
                        : undefined
                    }
                    errorId={"id" + errors.description}
                    invalid={Boolean(errors.description && touched.description)}
                  />
                </div>
              </div>

              <ExternalControlFormDocuments
                  controlUuid={controlUuid}
                  filesActive={mode === 'edit'}
                  projectUuid={projectUuid}
              />

              <div className='row'>
                <div className='col-md-6'>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={values.hasConsequences}
                        onChange={handleChange}
                        name='hasConsequences'
                      />
                    }
                    label={t('external-controls-financial-consequences')}
                  />
                </div>
              </div>
              {values.hasConsequences && (
                <ConsequencesFormHelper
                  consequencesPercents={consequencesPercents}
                  conseqeuncesTypes={consequencesTypes}
                  consequences={values.consequences}
                  setFieldValue={setFieldValue}
                />
              )}

              <div className={styles.actionButtonGap}>
                <FormActionButtons
                  abortPath='/external_controls_list'
                  disabled={disableSubmit}
                />
              </div>
            </Form>
          </>
        );
      }}
    </Formik>
  );
};
